import { Component, Input, Output, EventEmitter, ChangeDetectorRef, inject, OnInit, OnDestroy } from '@angular/core';
import { RunView, Metadata } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import {
    Blog, ContentSource, ContentTemplate, PipelineConfig, PipelineStepConfig,
    PipelineRunState, PipelineStepRunState, AgentStepType, ReviewFilter, Toast
} from '../../types';
import {
    loadAgentStepTypes, createSourceStep, createAgentStep,
    extractPipingData,
    savePipelineConfigs, loadPipelineConfigs
} from './content-studio.config';
import { buildAgentUserMessage, buildEnrichedInstructions } from './pipeline-user-message';

@Component({
    standalone: false,
    selector: 'mkt-pipeline-composer',
    templateUrl: './pipeline-composer.component.html',
    styleUrls: ['./pipeline-composer.component.css'],
})
export class PipelineComposerComponent implements OnInit, OnDestroy {
    private cdr = inject(ChangeDetectorRef);

    @Input() companyId: string = '';
    @Input() blogs: Blog[] = [];
    @Input() contentSources: ContentSource[] = [];
    @Input() contentTemplates: ContentTemplate[] = [];

    @Output() agentCompleted = new EventEmitter<{ outputType: string }>();
    @Output() viewChangeRequested = new EventEmitter<{ view: string; filter?: ReviewFilter }>();

    // ─── Pipeline Config ──────────────────────────
    pipelineName = 'New Pipeline';
    sourceStep: PipelineStepConfig = createSourceStep();
    agentSteps: PipelineStepConfig[] = [];
    agentStepTypes: AgentStepType[] = [];
    agentStepTypesLoading = true;

    // ─── UI State ─────────────────────────────────
    expandedStepId: string | null = null;
    showAddMenu = false;
    showLoadMenu = false;
    savedPipelines: PipelineConfig[] = [];

    // ─── Source Selection ─────────────────────────
    selectedBlogIds: string[] = [];
    selectedSourceIds: string[] = [];

    // ─── Run State ────────────────────────────────
    runState: PipelineRunState = {
        status: 'idle',
        currentStepIndex: 0,
        stepStates: [],
        accumulatedPayload: {},
    };
    private pollingTimer: ReturnType<typeof setInterval> | null = null;
    private running = false; // mutex to prevent concurrent runs
    private cancelled = false;
    private pipelineStartTime: number = 0;
    private elapsedTimer: ReturnType<typeof setInterval> | null = null;
    pipelineElapsed = '';
    pollingHeartbeat = false; // toggles on each poll for visual feedback

    // ─── Toasts ───────────────────────────────────
    toasts: Toast[] = [];
    private toastCounter = 0;

    showToast(type: Toast['type'], message: string, opts?: { icon?: string; action?: Toast['action']; autoDismissMs?: number }): void {
        const toast: Toast = {
            id: ++this.toastCounter,
            type,
            message,
            icon: opts?.icon || (type === 'success' ? 'fa-circle-check' : type === 'error' ? 'fa-triangle-exclamation' : 'fa-circle-info'),
            action: opts?.action,
            autoDismissMs: opts?.autoDismissMs ?? (type === 'error' ? 0 : 5000),
        };
        this.toasts.push(toast);
        this.cdr.detectChanges();

        if (toast.autoDismissMs && toast.autoDismissMs > 0) {
            setTimeout(() => this.dismissToast(toast.id), toast.autoDismissMs);
        }
    }

    dismissToast(id: number): void {
        this.toasts = this.toasts.filter(t => t.id !== id);
        this.cdr.detectChanges();
    }

    trackToast(_index: number, toast: Toast): number { return toast.id; }

    get currentRunningStepName(): string {
        const idx = this.runState.currentStepIndex;
        const allSteps = [this.sourceStep, ...this.agentSteps];
        return allSteps[idx]?.displayName || '';
    }

    get hasSourceSelected(): boolean {
        return this.selectedBlogIds.length > 0 || this.selectedSourceIds.length > 0;
    }

    get canRun(): boolean {
        return this.agentSteps.length > 0 && this.hasSourceSelected && !this.running;
    }

    async ngOnInit(): Promise<void> {
        this.loadSavedPipelines();
        // Dynamic agent discovery
        this.agentStepTypesLoading = true;
        try {
            this.agentStepTypes = await loadAgentStepTypes();
        } catch (err) {
            console.error('Failed to load agent types:', err);
            this.agentStepTypes = [];
        }
        this.agentStepTypesLoading = false;
        this.cdr.detectChanges();
    }

    ngOnDestroy(): void {
        this.stopPolling();
        this.stopElapsedTimer();
    }

    // ─── Step Management ──────────────────────────

    addStep(stepType: AgentStepType): void {
        this.agentSteps.push(createAgentStep(stepType));
        this.showAddMenu = false;
        this.cdr.detectChanges();
    }

    removeStep(index: number): void {
        this.agentSteps.splice(index, 1);
        if (this.expandedStepId && !this.agentSteps.find(s => s.id === this.expandedStepId)) {
            this.expandedStepId = null;
        }
        this.cdr.detectChanges();
    }

    moveStep(index: number, direction: -1 | 1): void {
        const target = index + direction;
        if (target < 0 || target >= this.agentSteps.length) return;
        const temp = this.agentSteps[index];
        this.agentSteps[index] = this.agentSteps[target];
        this.agentSteps[target] = temp;
        this.cdr.detectChanges();
    }

    toggleStepExpand(stepId: string): void {
        this.expandedStepId = this.expandedStepId === stepId ? null : stepId;
    }

    onSourceSelectionChanged(sel: { blogIds: string[]; sourceIds: string[] }): void {
        this.selectedBlogIds = sel.blogIds;
        this.selectedSourceIds = sel.sourceIds;
    }

    onStepInstructionsChanged(step: PipelineStepConfig, value: string): void {
        step.instructions = value;
    }

    async onTemplateBodyChanged(step: PipelineStepConfig, body: string): Promise<void> {
        if (!step.templateType) return;
        const template = this.contentTemplates.find(t =>
            t.TemplateType === step.templateType && t.IsActive
        );
        if (template) {
            template.TemplateBody = body;
            try {
                await template.Save();
            } catch (err) {
                console.error('Failed to save template:', err);
            }
        }
    }

    // ─── Save / Load ──────────────────────────────

    private loadSavedPipelines(): void {
        this.savedPipelines = loadPipelineConfigs().filter(p => p.companyId === this.companyId);
    }

    savePipeline(): void {
        const config: PipelineConfig = {
            id: crypto.randomUUID(),
            name: this.pipelineName || 'Untitled Pipeline',
            companyId: this.companyId,
            steps: [this.sourceStep, ...this.agentSteps],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        const all = loadPipelineConfigs();
        const existingIdx = all.findIndex(p => p.name === config.name && p.companyId === this.companyId);
        if (existingIdx >= 0) {
            config.id = all[existingIdx].id;
            config.createdAt = all[existingIdx].createdAt;
            all[existingIdx] = config;
        } else {
            all.push(config);
        }
        savePipelineConfigs(all);
        this.loadSavedPipelines();
        this.cdr.detectChanges();
    }

    loadPipeline(config: PipelineConfig): void {
        this.pipelineName = config.name;
        const steps = [...config.steps];
        this.sourceStep = steps.find(s => s.type === 'source') || createSourceStep();
        this.agentSteps = steps.filter(s => s.type === 'agent');
        // Reset selections when loading a different pipeline
        this.selectedBlogIds = [];
        this.selectedSourceIds = [];
        this.showLoadMenu = false;
        this.resetRunState();
        this.cdr.detectChanges();
    }

    deleteSavedPipeline(id: string): void {
        const all = loadPipelineConfigs().filter(p => p.id !== id);
        savePipelineConfigs(all);
        this.loadSavedPipelines();
        this.cdr.detectChanges();
    }

    // ─── Pipeline Execution ───────────────────────

    async runPipeline(): Promise<void> {
        if (!this.canRun) return;
        this.running = true;
        this.cancelled = false;
        this.pipelineStartTime = Date.now();
        this.startElapsedTimer();

        const allSteps = [this.sourceStep, ...this.agentSteps];
        this.runState = {
            status: 'running',
            currentStepIndex: 0,
            stepStates: allSteps.map(s => ({
                stepId: s.id,
                status: 'pending' as const,
            })),
            accumulatedPayload: {
                companyId: this.companyId,
                currentDate: new Date().toISOString().split('T')[0],
            },
        };

        this.cdr.detectChanges();
        await this.runFromStep(0);
        this.running = false;
    }

    private async runFromStep(startIdx: number): Promise<void> {
        const allSteps = [this.sourceStep, ...this.agentSteps];

        for (let i = startIdx; i < allSteps.length; i++) {
            if (this.cancelled) break;

            const step = allSteps[i];
            const stepState = this.runState.stepStates[i];
            this.runState.currentStepIndex = i;

            if (step.type === 'source') {
                stepState.status = 'running';
                stepState.startedAt = new Date();
                this.cdr.detectChanges();

                if (this.selectedBlogIds.length > 0) {
                    this.runState.accumulatedPayload['blogIds'] = this.selectedBlogIds;
                }
                if (this.selectedSourceIds.length > 0) {
                    this.runState.accumulatedPayload['contentSourceIds'] = this.selectedSourceIds;
                    this.runState.accumulatedPayload['contentSourceId'] = this.selectedSourceIds[0];
                }

                stepState.status = 'completed';
                stepState.completedAt = new Date();
                stepState.outputSummary = `${this.selectedBlogIds.length} blog(s), ${this.selectedSourceIds.length} source(s) selected`;
                this.cdr.detectChanges();
                continue;
            }

            // Agent step
            const result = await this.executeAgentStep(step, stepState);

            if (!result.success) {
                stepState.status = 'failed';
                stepState.error = result.error || 'Agent execution failed';
                this.runState.status = 'failed';

                for (let j = i + 1; j < allSteps.length; j++) {
                    this.runState.stepStates[j].status = 'skipped';
                }
                this.stopElapsedTimer();
                this.showToast('error', `Step failed: ${step.displayName} — ${stepState.error}`, {
                    icon: 'fa-circle-xmark',
                    autoDismissMs: 0,
                });
                this.cdr.detectChanges();
                return;
            }

            // Pipe output into accumulated payload
            if (result.outputData) {
                const pipedData = extractPipingData(step.agentName || '', result.outputData);
                Object.assign(this.runState.accumulatedPayload, pipedData);
            }

            // Step completion toast
            if (step.type === 'agent') {
                this.showToast('info', `${step.displayName} completed`, {
                    icon: 'fa-check',
                    autoDismissMs: 3000,
                });
            }
        }

        if (!this.cancelled) {
            this.runState.status = 'completed';
            const summary = this.buildOutputSummary(this.runState.accumulatedPayload);
            this.showToast('success', `Pipeline completed in ${this.pipelineElapsed}${summary ? ' — ' + summary : ''}`, {
                icon: 'fa-circle-check',
                autoDismissMs: 0,
                action: { label: 'View Results', callback: () => this.viewResults() },
            });
        }
        this.stopElapsedTimer();
        this.cdr.detectChanges();

        const lastAgent = this.agentSteps[this.agentSteps.length - 1];
        this.agentCompleted.emit({ outputType: lastAgent?.agentName || '' });
    }

    private async executeAgentStep(
        step: PipelineStepConfig,
        stepState: PipelineStepRunState
    ): Promise<{ success: boolean; error?: string; outputData?: Record<string, unknown> }> {
        stepState.status = 'running';
        stepState.startedAt = new Date();
        this.cdr.detectChanges();

        try {
            const gql = Metadata.Provider as GraphQLDataProvider;
            const rv = new RunView();

            // 1. Look up agent
            const agentResult = await rv.RunView({
                EntityName: 'MJ: AI Agents',
                ExtraFilter: `Name='${(step.agentName || '').replace(/'/g, "''")}'`,
                MaxRows: 1,
                ResultType: 'simple',
            });

            if (!agentResult?.Success || !agentResult.Results?.length) {
                return { success: false, error: `Agent "${step.agentName}" not found. Has metadata been synced?` };
            }

            const agentId = (agentResult.Results[0] as Record<string, unknown>)['ID'] as string;

            // 2. Build payload
            const instructions = buildEnrichedInstructions(step, this.runState.accumulatedPayload);
            const payload: Record<string, unknown> = {
                ...this.runState.accumulatedPayload,
                instructions,
                ...step.payloadOverrides,
            };

            // 3. User message — never empty; see buildAgentUserMessage for why MJ rejects
            // a blank one, and why a real message does not make a Flow agent conversational.
            const userMessage = buildAgentUserMessage(step, instructions);

            // 4. Fire agent
            const fireResult = await gql.ExecuteGQL(
                `mutation RunAIAgent($agentId: String!, $sessionId: String!, $messages: String!, $payload: String, $fireAndForget: Boolean) {
                    RunAIAgent(agentId: $agentId, sessionId: $sessionId, messages: $messages, payload: $payload, fireAndForget: $fireAndForget) { success errorMessage result }
                }`,
                {
                    agentId,
                    sessionId: `pipeline-${step.id}-${Date.now()}`,
                    messages: JSON.stringify([{ role: 'user', content: userMessage }]),
                    payload: JSON.stringify(payload),
                    fireAndForget: true,
                }
            );

            if (!fireResult?.RunAIAgent?.success) {
                return { success: false, error: fireResult?.RunAIAgent?.errorMessage || 'Failed to start agent' };
            }

            // 5. Poll until completion
            return await this.pollForCompletion(agentId, stepState);

        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Agent execution error';
            return { success: false, error: msg };
        }
    }

    private pollForCompletion(
        agentId: string,
        stepState: PipelineStepRunState
    ): Promise<{ success: boolean; error?: string; outputData?: Record<string, unknown> }> {
        const startTime = stepState.startedAt!.getTime();
        const maxPolls = 120; // 10 minutes at 5s intervals

        return new Promise((resolve) => {
            let pollCount = 0;

            this.pollingTimer = setInterval(async () => {
                if (this.cancelled) {
                    this.stopPolling();
                    resolve({ success: false, error: 'Cancelled' });
                    return;
                }

                pollCount++;
                this.pollingHeartbeat = !this.pollingHeartbeat;

                try {
                    const rv = new RunView();
                    const runResult = await rv.RunView({
                        EntityName: 'MJ: AI Agent Runs',
                        ExtraFilter: `AgentID='${agentId}' AND StartedAt >= '${new Date(startTime - 5000).toISOString()}'`,
                        OrderBy: 'StartedAt DESC',
                        MaxRows: 1,
                        ResultType: 'simple',
                    });

                    if (runResult.Success && runResult.Results?.length) {
                        const run = runResult.Results[0] as Record<string, unknown>;
                        stepState.agentRunId = run['ID'] as string;
                        const status = run['Status'] as string;

                        if (status === 'Completed') {
                            this.stopPolling();
                            stepState.status = 'completed';
                            stepState.completedAt = new Date();

                            const outputData = await this.extractRunOutput(stepState.agentRunId!);
                            stepState.outputSummary = this.buildOutputSummary(outputData);

                            resolve({ success: true, outputData });
                            this.cdr.detectChanges();
                            return;
                        } else if (status === 'Failed' || status === 'Cancelled') {
                            this.stopPolling();
                            const errorMsg = (run['ErrorMessage'] as string) || `Agent ${status.toLowerCase()}`;
                            resolve({ success: false, error: errorMsg });
                            this.cdr.detectChanges();
                            return;
                        }
                    }

                    if (pollCount >= maxPolls) {
                        this.stopPolling();
                        resolve({ success: false, error: 'Agent timed out after 10 minutes' });
                    }
                } catch (err) {
                    console.error('Polling error:', err);
                }
                this.cdr.detectChanges();
            }, 5000);
        });
    }

    private async extractRunOutput(runId: string): Promise<Record<string, unknown>> {
        try {
            const rv = new RunView();
            const stepsResult = await rv.RunView({
                EntityName: 'MJ: AI Agent Run Steps',
                ExtraFilter: `AgentRunID='${runId}'`,
                OrderBy: 'StepNumber DESC',
                MaxRows: 5,
                ResultType: 'simple',
            });

            if (stepsResult.Success && stepsResult.Results?.length) {
                for (const step of stepsResult.Results as Record<string, unknown>[]) {
                    const outputStr = step['OutputData'] as string | undefined;
                    if (outputStr) {
                        try {
                            return JSON.parse(outputStr);
                        } catch {
                            // Not JSON, continue
                        }
                    }
                }
            }
        } catch (err) {
            console.error('Failed to extract run output:', err);
        }
        return {};
    }

    private buildOutputSummary(data: Record<string, unknown>): string {
        const parts: string[] = [];
        if (data['newsletterId'] || data['NewsletterID']) parts.push('Newsletter created');
        if (data['subject']) parts.push(`"${data['subject']}"`);
        const socialIds = (data['socialPostIds'] || data['SocialPostIDs']) as string[] | undefined;
        if (socialIds?.length) parts.push(`${socialIds.length} social post(s)`);
        const blogIds = (data['savedBlogs'] as Record<string, unknown>)?.['blogIds'] as string[] | undefined;
        if (blogIds?.length) parts.push(`${blogIds.length} blog(s) saved`);
        return parts.join(' · ') || 'Completed';
    }

    cancelPipeline(): void {
        this.cancelled = true;
        this.stopPolling();
        this.stopElapsedTimer();
        this.runState.status = 'failed';

        for (const ss of this.runState.stepStates) {
            if (ss.status === 'running') ss.status = 'failed';
            if (ss.status === 'pending') ss.status = 'skipped';
        }
        this.running = false;
        this.cdr.detectChanges();
    }

    retryFromFailed(): void {
        if (this.running) return; // prevent concurrent runs
        const failedIdx = this.runState.stepStates.findIndex(s => s.status === 'failed');
        if (failedIdx < 0) return;

        for (let i = failedIdx; i < this.runState.stepStates.length; i++) {
            this.runState.stepStates[i].status = 'pending';
            this.runState.stepStates[i].error = undefined;
            this.runState.stepStates[i].outputSummary = undefined;
        }

        this.runState.status = 'running';
        this.running = true;
        this.cancelled = false;
        this.startElapsedTimer();
        this.cdr.detectChanges();

        this.runFromStep(failedIdx).finally(() => { this.running = false; });
    }

    viewResults(): void {
        const lastAgent = this.agentSteps[this.agentSteps.length - 1];
        let filter: ReviewFilter = 'all';
        if (lastAgent?.agentName?.includes('Newsletter')) filter = 'newsletters';
        else if (lastAgent?.agentName?.includes('Social')) filter = 'social-posts';
        this.viewChangeRequested.emit({ view: 'review-queue', filter });
    }

    // ─── Helpers ──────────────────────────────────

    getStepRunState(stepId: string): PipelineStepRunState | null {
        return this.runState.stepStates.find(s => s.stepId === stepId) || null;
    }

    private resetRunState(): void {
        this.runState = {
            status: 'idle',
            currentStepIndex: 0,
            stepStates: [],
            accumulatedPayload: {},
        };
    }

    private stopPolling(): void {
        if (this.pollingTimer != null) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
        this.pollingHeartbeat = false;
    }

    private startElapsedTimer(): void {
        this.stopElapsedTimer();
        this.pipelineStartTime = Date.now();
        this.elapsedTimer = setInterval(() => {
            const s = Math.floor((Date.now() - this.pipelineStartTime) / 1000);
            if (s < 60) this.pipelineElapsed = `${s}s`;
            else this.pipelineElapsed = `${Math.floor(s / 60)}m ${s % 60}s`;
            this.cdr.detectChanges();
        }, 1000);
    }

    private stopElapsedTimer(): void {
        if (this.elapsedTimer != null) {
            clearInterval(this.elapsedTimer);
            this.elapsedTimer = null;
        }
    }
}
